import React, { useRef, useState, useEffect } from 'react';
import { Icon } from 'semantic-ui-react';
import { Input, Label } from '../../input/input';
import { Toggle } from '../../input/toggle';

// Utility function for handling area editing
export const handleEditArea = (area, setAreaSelected, setOpenEditAreaModal) => {
  setAreaSelected(area);
  setOpenEditAreaModal(true);
};

// Utility function for getting current input value
export const getCurrentInputValue = (areaNameInputRef, areaSelected) => {
  return areaNameInputRef.current?.value || areaSelected?.label || '';
};

const EditAreaModal = ({
  areaSelected,
  incomeCategories,
  expenseCategories,
  formRef
}) => {
  const forms = useRef({});
  const [showCategoryAssignment, setShowCategoryAssignment] = useState(false);

  // Initialize form data when component mounts or areaSelected changes
  useEffect(() => {
    if (formRef && areaSelected) {
      formRef.current.areaName = areaSelected.label || '';
      formRef.current.categoryToAddAreas = null;
    }
  }, [formRef, areaSelected]);

  // Monitor toggle state changes
  useEffect(() => {
    const checkToggleState = () => {
      const toggleInput = forms.current?.categoryAssignmentToggle;
      if (toggleInput) {
        const isChecked = toggleInput.checked;
        setShowCategoryAssignment(isChecked);
        if (!isChecked && formRef) {
          formRef.current.categoryToAddAreas = null;
        }
      }
    };

    // Check initial state
    checkToggleState();

    // Set up an interval to check for changes since Toggle doesn't provide onChange
    const interval = setInterval(checkToggleState, 100);

    return () => clearInterval(interval);
  }, [formRef]);

  return (
    <div className="tw-h-[500px] tw-space-y-6">
      {/* Header with area info */}
      <div className="tw-flex tw-items-center tw-justify-center tw-gap-3 tw-pb-4 tw-border-b tw-border-gray-200">
        <div className="tw-p-2 tw-rounded-lg tw-bg-blue-100">
          <Icon name="edit" color="blue" size="large" />
        </div>
        <div className="tw-text-center">
          <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900">
            {areaSelected?.label || 'Editar área'}
          </h3>
          <p className="tw-text-sm tw-text-gray-600">
            Categoría: <span className="tw-font-medium">{areaSelected?.cashflowCategoryName || 'Sin categoría'}</span>
          </p>
        </div>
      </div>

      {/* Area name input */}
      <div className="">
        <Label value="Nuevo nombre del área" className="tw-text-sm" />
        <Input
          type="text"
          id="editAreaNameField"
          placeholder="Ingrese el nuevo nombre..."
          useRef={forms}
          defaultValue={areaSelected?.label || ''}
          className="tw-text-base"
          onChange={(e) => {
            if (formRef) {
              formRef.current.areaName = e.target.value;
            }
          }}
        />
        <p className="tw-text-xs tw-text-gray-500 mt-2">
          El nombre debe ser único y no puede repetirse
        </p>
      </div>

      {/* Toggle for category assignment */}
      <div className="tw-flex tw-items-center tw-justify-between tw-p-3 tw-bg-gray-50 tw-rounded-lg">
        <div className="tw-flex tw-items-center tw-gap-3">
          <Icon name="settings" color="blue" />
          <div>
            <h4 className="tw-text-sm tw-font-medium tw-text-gray-700">
              Mover área a otra categoría
            </h4>
            <p className="tw-text-xs tw-text-gray-500">Opcional - Selecciona una nueva categoría si quieres mover esta área</p>
          </div>
        </div>
        <Toggle
          id="categoryAssignmentToggle"
          className="tw-ml-4"
          useRef={forms}
        />
      </div>

      {/* Category assignment section - always rendered but conditionally visible */}
      <div className={`tw-space-y-4 ${showCategoryAssignment ? 'tw-opacity-100 tw-visible' : 'tw-opacity-0 tw-invisible tw-h-0 tw-overflow-hidden'}`}>
        {/* Categories list with better organization */}
        <div className="tw-space-y-3 tw-pl-6 tw-border-l-2 tw-border-blue-200 tw-bg-blue-50 tw-p-4 tw-rounded-lg">
          <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
            <Icon name="folder open" color="blue" />
            <h5 className="tw-text-sm tw-font-medium tw-text-gray-700">
              Categorías disponibles
            </h5>
          </div>
          
          <div className="tw-max-h-48 tw-overflow-y-auto tw-space-y-2">
            {(areaSelected?.isIncome ? incomeCategories : expenseCategories)?.map((category) => (
              <div 
                key={category.cashflowCategoryId}
                className={`tw-flex tw-items-center tw-gap-3 tw-p-3 tw-rounded-lg tw-border tw-transition-all ${
                  formRef?.current?.categoryToAddAreas?.cashflowCategoryId === category.cashflowCategoryId 
                    ? 'tw-bg-blue-100 tw-border-blue-300' 
                    : 'tw-bg-white tw-border-gray-200 hover:tw-bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  id={`edit-category-${category.cashflowCategoryId}`}
                  name="editSelectedCategory"
                  // checked={formRef?.current?.categoryToAddAreas?.cashflowCategoryId === category.cashflowCategoryId}
                  onChange={() => {
                    if (formRef) {
                      formRef.current.categoryToAddAreas = category;
                    }
                  }}
                  className="tw-w-4 tw-h-4 tw-text-blue-600 tw-border-gray-300 focus:tw-ring-blue-500"
                />
                <label 
                  htmlFor={`edit-category-${category.cashflowCategoryId}`}
                  className="tw-text-sm tw-text-gray-700 tw-cursor-pointer tw-flex-1 tw-flex tw-items-center tw-gap-2"
                >
                  <Icon 
                    name="chart area" 
                    color={areaSelected?.isIncome ? "green" : "red"} 
                    size="small" 
                  />
                  {category.label}
                </label>
              </div>
            ))}
            {(areaSelected?.isIncome ? incomeCategories : expenseCategories)?.length === 0 && (
              <div className="tw-p-4 tw-text-center tw-text-gray-500 tw-bg-white tw-rounded-lg">
                <Icon name="info circle" color="gray" />
                <p className="tw-mt-2">No hay categorías disponibles</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Current category info */}
      {/* {areaSelected && (
        <div className="tw-bg-gray-50 tw-p-3 tw-rounded-lg">
          <p className="tw-text-sm tw-text-gray-700 tw-flex tw-items-center tw-gap-2">
            <Icon name="info circle" color="blue" />
            Categoría actual: <span className="tw-font-medium">{areaSelected.cashflowCategoryName || 'Sin categoría'}</span>
          </p>
        </div>
      )} */}

      {/* Summary section */}
      {/* <div className="tw-bg-blue-50 tw-p-4 tw-rounded-lg tw-border tw-border-blue-200">
        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
          <Icon name="check circle" color="blue" />
          <span className="tw-text-sm tw-font-medium tw-text-gray-700">Resumen de cambios</span>
        </div>
        <div className="tw-text-xs tw-text-gray-600 tw-space-y-1">
          <p>• Nombre: <span className="tw-font-medium">{areaSelected?.label}</span> → <span className="tw-font-medium">{formRef?.current?.areaName || areaSelected?.label}</span></p>
          {showCategoryAssignment && formRef?.current?.categoryToAddAreas && (
            <p>• Categoría: <span className="tw-font-medium">{areaSelected.cashflowCategoryName || 'Sin categoría'}</span> → <span className="tw-font-medium">{formRef.current.categoryToAddAreas.label}</span></p>
          )}
        </div>
      </div> */}
    </div>
  );
};

export default EditAreaModal; 